// Boot of the design page: read the link, find the product, load its files, start the editor.
// If something fails, the page shows a plain message and the shop's product page still works.
// The product is one of the 2 test products (products/) or any live product by its handle (catalog.js).
// With adjust=1 the page is the "Adjust" editor of the "more gifts" step: it runs in a frame, starts from the design
// the page around it hands over, and gives the new design back (adjust.js).
// With embed=1 (and a valid parent=) the page is the full-screen layer over the store's product page: it says when it is ready,
// has a Close, and tells the store page when a design is saved, instead of redirecting (embed.js, embed-ui.js).

import { readParams, BootError } from "./products.js";
import { loadFromLink } from "./catalog.js";
import { initConfirm } from "./confirm.js";
import { initMoreGifts } from "./more-gifts.js";
import { initBackgrounds } from "./backgrounds.js";
import { initView3d } from "./view3d.js";
import { inFrame, sendDesign, waitForDesign } from "./adjust.js";
import { startEmbed } from "./embed.js";
import { initEmbedUi } from "./embed-ui.js";
import { fallbackInfo, loadStoreInfo, setStoreBridge } from "./store-sizes.js";
import { showState, hideState } from "./state.js";
import { $, listWords, shortTitle } from "./util.js";

const params = readParams();
const adjust = params.adjust && inFrame();           // the "Adjust" editor of the more gifts step (adjust.js)
const embed = adjust ? null : startEmbed({ handle: params.handle });      // the layer over the store's product page (embed.js), or null
const embedUi = embed ? initEmbedUi({ embed }) : null;
if (embed) { setStoreBridge(embed); embed.ready(); }                      // the store page answers with the live product, see store-sizes.js

// ---------- the loading / error screen ----------
const showMessage = (opts) => showState(opts, "app");
const reload = () => location.reload();
// In the layer, "Back to the shop" would open the shop inside the layer, so it is a Close instead.
const backToShop = () => (embed ? [{ label: "Close", onClick: () => embed.close() }] : params.returnUrl ? [{ label: "Back to the shop", href: params.returnUrl }] : []);

const ERRORS = {
  unknown_product: () => ({
    title: "We can't find this product",
    text: "This link may be old or wrong. Go back to the shop and tap Personalise again.",
    actions: backToShop()
  }),
  files: () => ({
    title: "We could not load this product",
    text: "Check your internet, then try again. Your shop still works.",
    actions: [{ label: "Try again", primary: true, onClick: reload }, ...backToShop()]
  }),
  editor: () => ({
    title: "The design tool could not start",
    text: "Your browser may be too old, or the phone is short of memory. Try again, or open this page in Chrome or Safari. Your shop still works.",
    actions: [{ label: "Try again", primary: true, onClick: reload }, ...backToShop()]
  })
};
const fail = (kind) => showMessage(ERRORS[kind]());

// ---------- boot ----------
async function boot() {
  if (adjust) {
    document.body.classList.add("adjusting");
    const done = $("actReview"); done.lastChild.textContent = "Use this"; done.setAttribute("aria-label", "Use this design");
  }
  const handed = adjust ? waitForDesign() : null;     // asks the page around us for the design to start from; the answer is read before the editor starts
  if (handed) handed.catch(() => {});                 // (a missing answer is handled below)
  // Ask the shop for the live sizes now, while the product loads. It is only needed for bulk orders and never fails the page.
  const liveSizes = adjust ? null : loadStoreInfo(params);
  let loaded;
  try {
    loaded = await loadFromLink(params);
  } catch (e) {
    if (!(e instanceof BootError)) console.error("RightGifting: product load failed", e);
    return fail(e instanceof BootError ? e.kind : "files");
  }
  const { product, sides } = loaded;         // sides: one for a single-sided product; Front | Back, or one for each item of a pack or a pair (up to 8)

  document.title = `${product.title} | RightGifting`;
  $("pageTitle").textContent = shortTitle(product.title);     // the full title stays in the browser tab
  $("stage").style.backgroundImage = `url("${sides[0].photoImg.src}")`;
  $("specSizes").textContent = product.sizes.length ? `one design for ${listWords(product.sizes)}` : "one design for every size";
  hideState("app");

  try {
    const { createEditor } = await import("./editor.js");    // loaded on its own so a failure here is caught below
    let editor = null, review = null, gifts = null;
    if (adjust) {
      const initial = await handed;                          // { sides, side, sources, originals }
      editor = await createEditor({
        product, sides, sizeInfo: fallbackInfo(product), liveSizes: null, initial, noDraft: true,
        onDone: async () => {
          if (editor.isEmpty()) { editor.toast("Add a photo or some text first, then tap Use this."); return; }
          sendDesign(await editor.exportDesign());
        }
      });
      return;
    }
    // Done opens the check at once; the gifts are a strip inside it (more-gifts.js).
    editor = await createEditor({ product, sides, sizeInfo: fallbackInfo(product), liveSizes, onDone: () => review && review.open() });
    if (embedUi) embedUi.setEditor(editor);
    const ctx = { shop: params.shop, product: product.id, handle: product.handle || params.handle, variant: params.variant, returnUrl: params.returnUrl, embed };
    gifts = initMoreGifts({ editor, ctx, onChange: () => review && review.giftsChanged() });
    // "See it in 3D" and the customer's own suitcase size (luggage covers). A size typed on the product page comes with the link.
    const view3d = initView3d({ editor, ctx, link: { csize: params.csize, cunit: params.cunit }, onChange: () => review && review.sizeChanged() });
    if (liveSizes) liveSizes.then(() => view3d.sync(), () => {});
    review = initConfirm({ editor, ctx, gifts, view3d });
    gifts.preload();
    // Background: another design of the same product type. The page then shows its name, and the link holds it, so a reload opens it.
    initBackgrounds({
      editor, ctx, liveSizes,
      onChange: (p) => {
        view3d.sync();                                       // a custom size keeps its price band on the new design
        document.title = `${p.title} | RightGifting`;
        $("pageTitle").textContent = shortTitle(p.title);
        try {
          const u = new URL(location.href);
          u.searchParams.set("handle", ctx.handle);
          if (ctx.variant) u.searchParams.set("variant", ctx.variant); else u.searchParams.delete("variant");
          history.replaceState(history.state, "", u.href);
        } catch (e) { /* the address stays as it was */ }
      }
    });
  } catch (e) {
    console.error("RightGifting: the editor could not start", e);
    fail("editor");
  }
}

boot().catch((e) => { console.error("RightGifting: unexpected error", e); fail("editor"); });
